import { useToggle } from '@vueuse/core';
import { ExceptionDetails, UserCatalogDto } from '@/api/generator';
import { NotificationMessage } from '@/components/notifications/types';
import { ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { hasValidationErrors } from '@/errorHandling';
import { useStoreErrorNotification } from '@/errorHandling/useStoreErrorNotification';
import { useExceptionDetails } from '@/errorHandling/useExceptionDetails';

/**
 * Composable for managing maintenance mode dialog UI state and exception handling
 * Does not manage form validation - that's handled by the parent component
 */
export function useMaintenanceModeDialog() {
  // Dialog state
  const [isOpen, toggleDialog] = useToggle(false);
  const { t } = useTranslation();

  const exceptionDetails = ref<ExceptionDetails | undefined>(undefined);
  const exceptionNotification = ref<NotificationMessage | undefined>(undefined);
  const storeNotification = ref<NotificationMessage | undefined>(undefined);
  const loggedInUsers = ref<UserCatalogDto[] | undefined>(undefined);

  // Error handling setup
  const { errorHandler, clearError, notificationMessage } = useStoreErrorNotification({
    showNotificationInToast: false,
    stopPropagation: false,
    notificationMessage: storeNotification,
    closable: false,
  });

  const dialogErrorHandler = (error: Error) => {
    if (hasValidationErrors(error)) return error;
    return errorHandler(error);
  };
  const { handleExceptionDetails, reset: resetExceptionDetails } = useExceptionDetails(
    t,
    exceptionDetails,
    exceptionNotification,
    loggedInUsers,
  );

  // Reset function to clear all state
  function reset() {
    clearError();
    resetExceptionDetails();
  }

  // Open dialog with clean state
  function openDialog() {
    reset();
    toggleDialog(true);
  }

  // Close dialog and reset state
  function closeDialog() {
    toggleDialog(false);
    reset();
  }

  return {
    // Dialog state
    isOpen,
    toggleDialog,
    openDialog,
    closeDialog,
    reset,

    // Exception handling
    exceptionDetails,
    exceptionNotification,
    notificationMessage,
    handleExceptionDetails,
    errorHandler: dialogErrorHandler,
    loggedInUsers,
  };
}
